import * as Sentry from "@sentry/browser";
import './lib/sentry';
import { ready } from './lib/dom';
import { log, isIframe } from './lib/util';
import UnitSelector from './lib/unit-selector';
import units from './lib/units';
import throttle from './lib/throttle';
import * as a from './actions';
import { placement } from './lib/epom';
import { jitter } from './lib/random';
import { identify, init as initAnalytics, pageview } from './lib/analytics';
import { isBlockingAds } from './lib/block-detection';

const unitSelector = new UnitSelector(units);
const reloadInterval = 60 * 1000;
const reloadEnabled = false;

interface User {
  userId: string,
  installId: string,
  preferences: string,
  version: string,
  roles: string,
}

let user: User = {
  userId: '',
  installId: '',
  preferences: '',
  version: '',
  roles: '',
};

let isError: boolean = false;
let inserted: boolean = false;
let reloadTimer: number;
let isClosing = false;

function identifyBrowser(): string {
  // @ts-ignore
  if (!!navigator.isGener8 || navigator.userAgent.includes('Gener8')) {
    return 'Gener8';
  }

  if (navigator.userAgent.includes('Chrome')) {
    return 'Chrome';
  }

  if (navigator.userAgent.includes('Firefox')) {
    return 'Firefox';
  }

  return 'Unknown';
}

function scheduleReload() {
  clearTimeout(reloadTimer);

  reloadTimer = window.setTimeout(() => {
    if (document.visibilityState !== 'visible') {
      scheduleReload();
      return;
    }

    inserted = false;
    insert(user);
  }, jitter(reloadInterval, 0.165));
}

function setInserted(state: boolean) {
  inserted = state;

  window.parent.postMessage({
    action: a.TOGGLE_FRAME,
    data: {
      inserted,
      frameName: window.name,
    }
  }, '*');

  if (inserted && reloadEnabled) {
    scheduleReload();
  }

  return state;
}

async function insert(params?: User): Promise<boolean> {
  log('insert');

  if (inserted) {
    return true;
  }

  const { x, y, key } = unitSelector.bestFit(window.innerWidth, window.innerHeight);

  log(`${window.innerWidth}x${window.innerHeight} placement \n${x}x${y} best fit`);

  if (params?.userId.length) {
    identify(params.userId);
  }

  if (!key) {
    pageview('unmatched');
    return setInserted(false);
  }

  const id = (params?.userId.length) ? params.userId : params?.installId;
  const idType = (params?.userId.length) ? 'user' : 'install';

  if (isClosing) {
    return setInserted(false);
  }

  if (id && await throttle(id, idType)) {
    pageview('throttled');
    return setInserted(false);
  }

  const $elem = document.querySelector('#root .container') as HTMLElement;

  if (!$elem || isClosing) {
    return setInserted(false);
  }

  const $ad = await placement({
    key,
    browser: identifyBrowser(),
    ...params,
  }).catch(err => {
    if (isClosing) {
      return null;
    }
    Sentry.captureException(err);
    pageview('error');
    isError = true;
    return null;
  });

  if (!$ad || isClosing) {
    return setInserted(false);
  }

  pageview(unitSelector.lookup(key) || 'unmatched');

  $elem.innerHTML = '';
  $ad.childNodes.forEach($node => {
    $elem.appendChild($node);
  });

  return setInserted(!isClosing);
}

window.addEventListener('message', async (ev) => {
  switch (ev.data.action) {
    case a.PROFILE_DATA:
      const user = ev.data.data as User;
      Sentry.setTag('extension.version', user.version || 'unknown');
      Sentry.setTag('authenticated', !!user.userId?.length);

      if (!await insert(user)) {
        window.addEventListener('resize', () => {
          insert(user);
        });
      }

      break;

    default:
      break;
  }
}, false);

function detectBlocking() {
  Sentry.setTag('adblocking', isBlockingAds());
}

(async () => {
  await ready();
  detectBlocking();
  initAnalytics();

  if (!isIframe()) {
    insert();
    return;
  }

  window.parent.postMessage({
    action: a.FRAME_READY,
    data: {
      frameName: window.name
    },
  }, '*');

  window.addEventListener('beforeunload', () => {
    isClosing = true;
    let eventLabel = 'Unviewed';

    if (isError) {
      eventLabel = 'Error';
    }

    if (inserted) {
      eventLabel = 'Viewed';
    }

    Sentry.addBreadcrumb({
      category: 'telemetry',
      message: 'Page unloading',
      level: Sentry.Severity.Info,
    });

    ga('send', 'event', {
      eventCategory: 'Window',
      eventAction: 'close',
      eventLabel,
      transport: 'beacon'
    });
  });
})();
